import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";
import { tokens } from "../../theme";

function ViewPaymentById() {
  const { id } = useParams();
  const theme = useTheme();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    fetchPaymentDetails();
  }, [id]);

  const fetchPaymentDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Payment log list IDs come from payment_logs — use paymentlog API, not Stripe history
      const response = await authFetch(`/paymentlog/getPaymentById/${id}`);
      const responseData = await response.json();
      const ok = responseData.success === true || responseData.status === true;

      if (ok && responseData.result) {
        const row = responseData.result;
        const amount = Number(row.price);

        setPaymentDetails({
          id: row.id,
          userId: row.userId,
          userName: row.User?.profileName || "N/A",
          email: row.User?.email || "N/A",
          description: row.description || "N/A",
          paidFor: row.paidFor || "N/A",
          amount: Number.isFinite(amount)
            ? `$${amount.toFixed(2)}`
            : row.price ?? "N/A",
          paymentIntent: row.paymentIntent || "N/A",
          status: "Succeeded",
          date: row.createdAt || row.updatedAt,
          createdAt: row.createdAt,
          updatedAt: row.updatedAt,
        });
      } else {
        setError(
          "Failed to fetch payment details: " +
            (responseData.message || responseData.error || "Unknown error")
        );
      }
    } catch (err) {
      setError("Error fetching payment details: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const formatFieldName = (field) => {
    return field.replace(/([A-Z])/g, " $1").trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!paymentDetails) {
    return (
      <Box m="20px">
        <Typography variant="h6">No payment details found.</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header title={`View Payment Log ID: ${id}`} subtitle="" />
      <Box ml={"40px"}>
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            mb={2}
            color={colors.blueAccent[400]}
          >
            Payment Details
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(paymentDetails).map(([field, value]) => {
              if (value === null || value === "") {
                return null;
              }
              const displayValue =
                field === "date" ||
                field === "createdAt" ||
                field === "updatedAt"
                  ? formatDate(value)
                  : value;

              return (
                <Grid item xs={12} key={field}>
                  <Grid container>
                    <Grid item xs={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {capitalizeFirstLetter(formatFieldName(field))}:
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <Typography variant="subtitle1">:</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography>{displayValue}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}

export default ViewPaymentById;
